/**
 * SignedFetch.ts
 * 
 * This composable is responsible for the secure signing of requests to the Cirrus CDN.
 * It forms part of the security implementations found here: 
 * https://docs.google.com/document/d/1yfjG1p1R0tayjFt6R3mfO5Iv6CAFRpWTshuOTuc_tyg/edit?usp=sharing (specifically Sections 5 and 6)
 * 
 * The functionality relies on a device-issued WebCrypto key pair to sign each request.
 */

export type SignedFetchOptions = {
  /** Provide Range header value, e.g. "bytes=0-1023" */
  range?: string;

  /** Provide Authorization header value, e.g. "Bearer <token>" */
  authorization?: string;

  /** Extra headers to include in the request */
  headers?: Record<string, string>;

  /** Fetch init overrides. Headers will be merged safely. */
  fetchInit?: Omit<RequestInit, "headers">;
};

type SignedFetchResult = {
  response: Response;
  signatureB64: string;
  publicKeyB64: string;
  canonicalPayload: string;
};

type SignedFetchBodyResult<T> = SignedFetchResult & {
  body: T;
};

const useLongLoadingIndicator: boolean = false;
const _activeRequestCount = ref(0);
const te = new TextEncoder();
let _cachedKeyPair: CryptoKeyPair | null = null;
let _cachedKeyPairPromise: Promise<CryptoKeyPair> | null = null;
let _loadingIndicatorDebounce: NodeJS.Timeout | undefined = undefined;
let _loadingIndicatorStartedBySignedFetch = false;


/**
 * Node Verify on the server requires a DER formatted signature
 * This function performs the conversion between a raw ECDSA signature and DER.
 * 
 * @param raw Uint8Array
 * @returns Uint8Array
 */
function rawEcdsaSigToDer(raw: Uint8Array): Uint8Array {
    // raw = r||s (P-256 => 64 bytes)
    const half = raw.length / 2;
    if (!Number.isInteger(half)) throw new Error("Invalid raw ECDSA signature length");

    const r = raw.slice(0, half);
    const s = raw.slice(half);

    const trim = (x: Uint8Array) => {
        let i = 0;
        while (i < x.length - 1 && x[i] === 0) i++;
        return x.slice(i);
    };

    const ensurePositive = (x: Uint8Array): Uint8Array => {
        // Grab first byte via iterator (avoids x[0] typing issues)
        const it = x.values().next();
        if (it.done) {
            throw new Error("Invalid ECDSA integer: empty buffer");
        }
        const firstByte = it.value; // number

        // If high bit set, prefix 0x00 to indicate positive integer
        if ((firstByte & 0x80) !== 0) {
            const out = new Uint8Array(x.length + 1);
            out[0] = 0x00;
            out.set(x, 1);
            return out;
        }

        return x;
    };

    const mkInt = (x: Uint8Array) => {
        const t = ensurePositive(trim(x));
        const out = new Uint8Array(2 + t.length);
        out[0] = 0x02; // INTEGER
        out[1] = t.length;
        out.set(t, 2);
        return out;
    };

    const rInt = mkInt(r);
    const sInt = mkInt(s);

    const len = rInt.length + sInt.length;
    if (len > 255) throw new Error("DER length too long for simple encoder");

    const seq = new Uint8Array(2 + len);
    seq[0] = 0x30; // SEQUENCE
    seq[1] = len;
    seq.set(rInt, 2);
    seq.set(sInt, 2 + rInt.length);

    return seq;
}


/**
 * Get headers helper function, normalised to lowercase
 * 
 * @param headers Record<string, string> | undefined
 * @param name string
 * @returns string
 */
function getHeader(headers: Record<string, string> | undefined, name: string): string {
  if (!headers) return "";
  const match = Object.entries(headers).find(([key]) => key.toLowerCase() === name.toLowerCase());
  return match?.[1] ?? "";
}


/**
 * Canonicalise headers to ensure accurate verification on the server
 * 
 * @param input 
 * @returns string
 */
function canonicalizeHeaders(input: {
    range?: string;
    authorization?: string;
    xCirrusKey: string;
    xAuthorization: string;
}): string {
    return [
        `range:${input.range ?? ""}`,
        `authorization:${input.authorization ?? ""}`,
        `x-authorization:${input.xAuthorization ?? ""}`,
        `x-cirrus-key:${input.xCirrusKey ?? ""}`,
    ].join("\n");
}


/**
 * Helper function to convert an ArrayBuffer to Base64
 * 
 * @param ab ArrayBuffer
 * @returns string
 */
function abToB64(ab: ArrayBuffer): string {
    const bytes = new Uint8Array(ab);
    let bin = "";

    for (const byte of bytes) {
        bin += String.fromCharCode(byte);
    }

    return btoa(bin);
}


/**
 * Function to generate a client-side ECDSA Key Pair using the Node crypto library.
 * Permits signing and verifying using the library.
 * 
 * @returns Promise<CryptoKeyPair>
 */
async function generateSigningKeyPair(): Promise<CryptoKeyPair> {
    return crypto.subtle.generateKey(
        { name: "ECDSA", namedCurve: "P-256" },
        true,
        ["sign", "verify"]
    );
}

/**
 * Function to export the generated public key in SPKI format, returns at Base64.
 * 
 * @param publicKey CryptoKey
 * @returns Promise<string>
 */
async function exportPublicKeySpkiB64(publicKey: CryptoKey): Promise<string> {
    const spki = await crypto.subtle.exportKey("spki", publicKey);
    return abToB64(spki);
}


/**
 * Function to sign the payload using the generated private key
 * 
 * @param privateKey CryptoKey - Generated using generateSigningKeyPair()
 * @param canonicalPayload string
 * @returns Promise<string>
 */
async function signCanonicalPayload(
    privateKey: CryptoKey,
    canonicalPayload: string
): Promise<string> {
    const sig = await crypto.subtle.sign(
        { name: "ECDSA", hash: "SHA-256" },
        privateKey,
        te.encode(canonicalPayload)
    );

    const sigBytes = new Uint8Array(sig);

    // If it looks like raw P-256 (64 bytes), convert to DER for Node verify
    const derBytes = sigBytes.length === 64 ? rawEcdsaSigToDer(sigBytes) : sigBytes;

    return btoa(String.fromCharCode(...derBytes));
}


/**
 * Function to cache key pair to prevent unneccessary key generation in the browser.
 * Stores _cachedKeyPairPromise too to prevent race behaviour generating two key pairs.
 * 
 * @returns Promise<CryptoKeyPair>
 */
async function getSigningKeyPair(): Promise<CryptoKeyPair> {
  if (_cachedKeyPair) return _cachedKeyPair;
  if (_cachedKeyPairPromise) return _cachedKeyPairPromise;

  _cachedKeyPairPromise = generateSigningKeyPair().then((keyPair) => {
    _cachedKeyPair = keyPair;
    return keyPair;
  }).finally(() => {
    _cachedKeyPairPromise = null;
  });

  return _cachedKeyPairPromise;
}

/**
 * Nuxt composable that returns a signedFetch() helper.
 *
 * Notes:
 * - Client-side only (requires WebCrypto).
 * - Signs a canonical payload that includes Range + Authorization + x-public-key.
 * - Sends x-public-key + x-signature headers.
 */
export const useSignedFetch = () => {

    const isClient = computed(() => import.meta.client);
    const isFetching = computed(() => _activeRequestCount.value > 0);

    function scheduleLoadingIndicatorStart(delayMs: number): void {
        const nuxtApp = useNuxtApp();
        const loadingIndicator = useLoadingIndicator();

        if (_loadingIndicatorDebounce !== undefined) {
            return;
        }

        _loadingIndicatorDebounce = setTimeout(() => {
            _loadingIndicatorDebounce = undefined;

            if (_activeRequestCount.value <= 0 || _loadingIndicatorStartedBySignedFetch) {
                return;
            }

            if (nuxtApp.isHydrating) {
                scheduleLoadingIndicatorStart(100);
                return;
            }

            _loadingIndicatorStartedBySignedFetch = true;
            loadingIndicator.start();
        }, delayMs);
    }

    function onFetchStarted(): void {
        if (_activeRequestCount.value !== 1 || _loadingIndicatorStartedBySignedFetch) {
            return;
        }

        scheduleLoadingIndicatorStart(1000);
    }

    function onFetchFinished(): void {
        const loadingIndicator = useLoadingIndicator();

        if (_activeRequestCount.value !== 0) {
            return;
        }

        if (_loadingIndicatorDebounce !== undefined) {
            clearTimeout(_loadingIndicatorDebounce);
            _loadingIndicatorDebounce = undefined;
        }

        if (_loadingIndicatorStartedBySignedFetch) {
            _loadingIndicatorStartedBySignedFetch = false;
            loadingIndicator.finish();
        }
    }

    function beginTrackedFetch(): void {
        if (useLongLoadingIndicator){
            _activeRequestCount.value++;
            onFetchStarted();
        }
    }

    function endTrackedFetch(): void {
        if (useLongLoadingIndicator){
            _activeRequestCount.value--;
            onFetchFinished();
        }
    }

    /**
     * Create a signed request, not to be called directly.
     * Call either signedFetch or signedFetchWithResponseBody, signedFetchJson or signedFetchArrayBuffer.
     * 
     * @param url string
     * @param opts SignedFetchOptions
     * @returns Promise<SignedFetchResult>
     */
    async function createSignedRequest(
        url: string,
        opts: SignedFetchOptions = {}
    ): Promise<SignedFetchResult> {
        if (!import.meta.client) {
            throw new Error("signedFetch() must be called client-side (WebCrypto required).");
        }
        if (!globalThis.crypto?.subtle) {
            throw new Error("WebCrypto SubtleCrypto not available in this environment.");
        }

        const range = opts.range ?? "";
        const authorization = opts.authorization ?? "";
        const keyPair = await getSigningKeyPair();
        const publicKeyB64 = await exportPublicKeySpkiB64(keyPair.publicKey);
        const canonicalPayload = canonicalizeHeaders({
            range,
            authorization,
            xAuthorization: getHeader(opts.headers, 'x-authorization'),
            xCirrusKey: publicKeyB64,
        });

        const signatureB64 = await signCanonicalPayload(
            keyPair.privateKey,
            canonicalPayload
        );

        // Merge headers (user headers override ONLY non-security fields)
        const mergedHeaders: Record<string, string> = {
            ...(opts.headers ?? {}),
            ...(range ? { Range: range } : {}),
            ...(authorization ? { Authorization: authorization } : {}),
            "x-cirrus-key": publicKeyB64,
            "x-cirrus-signature": signatureB64,
        };

        const response = await fetch(url, {
            method: "GET",
            cache: "no-store",
            ...(opts.fetchInit ?? {}),
            headers: mergedHeaders,
        });

        return {
            response,
            signatureB64,
            publicKeyB64,
            canonicalPayload,
        };
    }

    async function signedFetch(
        url: string,
        opts: SignedFetchOptions = {}
    ): Promise<SignedFetchResult> {
        try {
            beginTrackedFetch();
            try {
                return await createSignedRequest(url, opts);
            } finally {
                endTrackedFetch();
            }
        } catch (err) {
            console.error("signedFetch() failed with error:", err);
            throw err;
        }
    }

    async function signedFetchWithResponseBody<T>(
        url: string,
        opts: SignedFetchOptions,
        readBody: (_response: Response) => Promise<T>
    ): Promise<SignedFetchBodyResult<T>> {
        try {
            beginTrackedFetch();
            try {
                const result = await createSignedRequest(url, opts);
                const body = await readBody(result.response);

                return {
                    ...result,
                    body,
                };
            } finally {
                endTrackedFetch();
            }
        } catch (err) {
            console.error("signedFetchWithResponseBody() failed with error:", err);
            throw err;
        }
    }

    async function signedFetchJson<T>(
        url: string,
        opts: SignedFetchOptions = {}
    ): Promise<SignedFetchBodyResult<T>> {
        return signedFetchWithResponseBody(url, opts, async (response) => await response.json() as T);
    }

    async function signedFetchArrayBuffer(
        url: string,
        opts: SignedFetchOptions = {}
    ): Promise<SignedFetchBodyResult<ArrayBuffer>> {
        return signedFetchWithResponseBody(url, opts, async (response) => await response.arrayBuffer());
    }

  return {
    isClient,
    signedFetch,
    signedFetchWithResponseBody,
    signedFetchJson,
    signedFetchArrayBuffer,
    isFetching
  };
};
